import React, { useState } from 'react';
import './ComboSelectPage.css';

export default function ComboSelectPage({ item, cart, onAddToCart, onUpdateQuantity, onBack, onOpenMealDrinkPage }) {
  const allOptions = (item.combo_options || '').split('|').map(o => {
    const isAddon = o.startsWith('+');
    const raw = isAddon ? o.slice(1) : o;
    const [name, extra] = raw.split(':');
    return { name: name.trim(), extra: parseInt(extra) || 0, isAddon };
  }).filter(o => o.name);

  const breadOptions = allOptions.filter(o => !o.isAddon);
  const addonOptions = allOptions.filter(o => o.isAddon);

  const existingItems = cart.filter(c => c.id === item.id);

  const [selectedBread, setSelectedBread] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState(new Set());
  const [mealDrink, setMealDrink] = useState(null); // { displayName, extraPrice }

  const hasMealSet = selectedAddons.has('小鳥胃');

  const toggleAddon = name => {
    setSelectedAddons(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
    if (name === '小鳥胃') setMealDrink(null);
  };

  const selectMealDrinkQuick = (name) => {
    setMealDrink({ displayName: name, extraPrice: 0 });
  };

  const openOtherDrinks = () => {
    onOpenMealDrinkPage(drinkInfo => setMealDrink(drinkInfo));
  };

  const selectedBreadObj = breadOptions.find(b => b.name === selectedBread);
  const addonExtra = addonOptions
    .filter(a => selectedAddons.has(a.name))
    .reduce((sum, a) => sum + a.extra, 0);
  const mealDrinkExtra = mealDrink?.extraPrice || 0;
  const totalExtra = (selectedBreadObj?.extra || 0) + addonExtra + mealDrinkExtra;
  const totalPrice = item.price + totalExtra;
  const canAdd = selectedBread !== null && (!hasMealSet || mealDrink !== null);

  const handleAdd = () => {
    if (!canAdd) return;
    const addonSuffix = addonOptions
      .filter(a => selectedAddons.has(a.name))
      .map(a => a.name === '小鳥胃' && mealDrink ? `小鳥胃+${mealDrink.displayName}` : a.name)
      .join('+');
    const suffix = addonSuffix ? `${selectedBread}+${addonSuffix}` : selectedBread;
    const cartKey = `${item.id}_${suffix}`;
    const displayName = `${item.name} (${suffix})`;
    onAddToCart({ id: item.id, cartKey, displayName, price: totalPrice, size: suffix });
    setSelectedBread(null);
    setSelectedAddons(new Set());
    setMealDrink(null);
  };

  return (
    <div className="combo-page">
      <div className="combo-page-header">
        <button className="combo-page-back" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
          返回菜單
        </button>
      </div>

      <div className="combo-page-body">
        <div className="combo-page-item-name">{item.name}</div>
        {item.description && (
          <div className="combo-page-item-desc">{item.description}</div>
        )}

        {existingItems.length > 0 && (
          <div className="combo-existing-section">
            <div className="combo-page-section-label">已選</div>
            <div className="combo-existing-list">
              {existingItems.map(ci => (
                <div key={ci.cartKey} className="combo-existing-row">
                  <span className="combo-existing-label">{ci.size || ci.displayName}</span>
                  <div className="qty-ctrl">
                    <button onClick={() => onUpdateQuantity(ci.cartKey, -1)}>−</button>
                    <span>{ci.quantity}</span>
                    <button onClick={() => onUpdateQuantity(ci.cartKey, 1)}>+</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="combo-page-section-label">
          {existingItems.length > 0 ? '再加一份・選麵包' : '選擇麵包'}
        </div>
        <div className="combo-bread-options">
          {breadOptions.map(b => (
            <button
              key={b.name}
              className={`combo-option-btn${selectedBread === b.name ? ' selected' : ''}`}
              onClick={() => setSelectedBread(b.name)}
            >
              {b.name}{b.extra > 0 ? <span className="combo-extra-price"> +{b.extra}</span> : ''}
            </button>
          ))}
        </div>

        {addonOptions.length > 0 && (
          <>
            <div className="combo-page-section-label">加點（可複選）</div>
            <div className="combo-addon-row">
              {addonOptions.map(a => (
                <button
                  key={a.name}
                  className={`combo-addon-btn${selectedAddons.has(a.name) ? ' selected' : ''}`}
                  onClick={() => toggleAddon(a.name)}
                >
                  {selectedAddons.has(a.name) ? '✓ ' : ''}{a.name}
                  <span className="combo-extra-price"> +{a.extra}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {hasMealSet && (
          <>
            <div className="combo-page-section-label">
              套餐飲料
              {!mealDrink && <span className="combo-required-hint">（必選）</span>}
            </div>
            <div className="combo-meal-drink-row">
              {['紅茶（冰）', '紅茶（熱）'].map(name => (
                <button
                  key={name}
                  className={`combo-meal-drink-btn${mealDrink?.displayName === name ? ' selected' : ''}`}
                  onClick={() => selectMealDrinkQuick(name)}
                >
                  {mealDrink?.displayName === name ? '✓ ' : ''}{name}
                </button>
              ))}
              <button
                className={`combo-meal-drink-btn other${mealDrink?.extraPrice > 0 ? ' selected' : ''}`}
                onClick={openOtherDrinks}
              >
                {mealDrink?.extraPrice > 0
                  ? `✓ ${mealDrink.displayName}`
                  : '其他飲品 →'}
              </button>
            </div>
          </>
        )}

        <div className="combo-price-row">
          <span className="combo-price-label">小計</span>
          <span className="combo-price-value">
            NT$ {totalPrice}
            {totalExtra > 0 && <span className="combo-price-extra"> ({item.price} + {totalExtra})</span>}
          </span>
        </div>

        <button
          className={`combo-add-btn${canAdd ? '' : ' disabled'}`}
          onClick={handleAdd}
          disabled={!canAdd}
        >
          {!selectedBread ? '請先選擇麵包'
            : hasMealSet && !mealDrink ? '請選擇套餐飲料'
            : '加入'}
        </button>
      </div>

      <div className="combo-page-footer">
        <button className="combo-confirm-btn" onClick={onBack}>完成</button>
      </div>
    </div>
  );
}
